import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';

// Star icon (full/half/empty) to match gold + outline style
const Star = ({ type = 'full', className = 'w-5 h-5' }) => {
  if (type === 'half') {
    return (
      <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
        <defs>
          <linearGradient id="half-fill" x1="0" x2="1">
            <stop offset="50%" stopColor="#ffb400" />
            <stop offset="50%" stopColor="transparent" />
          </linearGradient>
        </defs>
        <path
          d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.7 4.7 17.6l1-5.8L1.5 7.7l5.9-.9L10 1.5z"
          fill="url(#half-fill)"
          stroke="#ffb400"
          strokeWidth="1"
        />
      </svg>
    );
  }
  if (type === 'empty') {
    return (
      <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
        <path
          d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.7 4.7 17.6l1-5.8L1.5 7.7l5.9-.9L10 1.5z"
          fill="transparent"
          stroke="#475569"
          strokeWidth="1"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <path
        d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.7 4.7 17.6l1-5.8L1.5 7.7l5.9-.9L10 1.5z"
        fill="#ffb400"
        stroke="#ffb400"
        strokeWidth="1"
      />
    </svg>
  );
};

const Stars = ({ rating }) => {
  const full = Math.floor(rating);
  const frac = rating - full;
  const hasHalf = frac >= 0.25 && frac < 0.75;
  const items = [];
  for (let i = 0; i < full; i++) items.push(<Star key={`f-${i}`} className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5" />);
  if (hasHalf) items.push(<Star key="h" type="half" className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5" />);
  while (items.length < 5) items.push(<Star key={`e-${items.length}`} type="empty" className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5" />);
  return <div className="mt-1 sm:mt-2 flex items-center justify-center gap-0.5 sm:gap-1">{items}</div>;
};

const formatNum = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

const Card = ({ player, idx, onClick }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const hue = Math.round((idx % 24) * (360 / 24));
  const initials = player.name
    .split(' ')
    .map((s) => s[0])
    .join('')
    .slice(0, 2);

  return (
    <button
      type="button"
      className="group min-w-0 rounded-lg border px-2 py-4 sm:px-4 sm:py-5 md:px-5 md:py-6 text-center cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#111318] hover:border-white/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ffb400] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b0c10]"
      style={{
        backgroundColor: '#000',
        borderColor: 'rgba(255,255,255,0.22)',
      }}
      onClick={onClick}
      aria-label={`Open reviews for ${player.name}`}
    >
      <div
        className="mx-auto mb-3 sm:mb-4 grid place-items-center rounded-full overflow-hidden border-2 border-white/80 bg-slate-800 shadow-[0_0_0_4px_rgba(255,180,0,0.12),0_8px_22px_rgba(0,0,0,0.45)] transition-transform duration-200 group-hover:scale-[1.03]"
        style={{
          width: 'clamp(92px, 30vw, 148px)',
          height: 'clamp(92px, 30vw, 148px)',
          background: `radial-gradient(circle at 32% 30%, hsl(${hue} 85% 70%), hsl(${hue} 70% 45%))`,
        }}
      >
        {player.image && !imageFailed ? (
          <img
            src={player.image}
            alt=""
            className="h-full w-full object-cover"
            loading={idx < 4 ? 'eager' : 'lazy'}
            decoding="async"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <span className="text-xl sm:text-2xl font-extrabold text-black/70">{initials}</span>
        )}
      </div>
      <div className="flex min-h-10 items-center justify-center break-words text-[15px] sm:text-lg md:text-xl lg:text-2xl font-semibold leading-tight">{player.name}</div>
      <Stars rating={player.rating || 0} />
      <div className="mt-1 text-xs sm:text-sm text-slate-300">{formatNum(player.ratingsCount || 0)} ratings</div>
    </button>
  );
};

const PlayerReview = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const titleTop = 'PLAYER';
  const titleBottom = 'REVIEWS';

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/review-players`);
      if (!response.ok) throw new Error('Failed to fetch players');
      const data = await response.json();
      setPlayers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching players:', error);
      setLoadError('Players could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const handleCardClick = (player) => {
    navigate(`/player-detail/${player._id}`, { state: { player } });
  };

  if (loading) {
    return (
      <div
        className="relative min-h-screen text-white flex items-center justify-center"
        style={{
          backgroundColor: '#0b0c10',
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.09) 1px, transparent 1px), radial-gradient(rgba(255,255,255,0.24) 1px, transparent 1.4px)',
          backgroundSize: '32px 32px, 32px 32px, 32px 32px',
          backgroundPosition: 'center center, center center, 16px 16px',
        }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-white text-xl">Loading players...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen text-white overflow-x-hidden"
      style={{
        backgroundColor: '#0b0c10',
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.09) 1px, transparent 1px), radial-gradient(rgba(255,255,255,0.24) 1px, transparent 1.4px)',
        backgroundSize: '32px 32px, 32px 32px, 32px 32px',
        backgroundPosition: 'center center, center center, 16px 16px',
      }}
    >
      {/* Header block - Fully responsive */}
      <header className="relative pt-3 sm:pt-6 md:pt-8 px-3 sm:px-4">
        <div className="mb-3 sm:mb-0 sm:absolute sm:top-4 sm:left-4 sm:z-50">
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center border border-white/30 text-white hover:bg-white/10 hover:border-white/50 font-medium text-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded transition-all backdrop-blur-sm"
          >
            <svg className="w-3 h-3 mr-1.5 sm:w-4 sm:h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="sm:hidden">Back</span>
            <span className="hidden sm:inline">Back to Home</span>
          </button>
        </div>

        <div className="text-center">
          {/* Main title - Responsive font sizes */}
          <div className="mx-auto flex items-center justify-center gap-2 sm:gap-4 md:gap-6 font-black leading-none tracking-wide [text-shadow:0_2px_0_rgba(0,0,0,.65)]">
            <span 
              className="text-[#ffb400]"
              style={{ fontSize: 'clamp(2rem, 8vw, 8rem)' }}
            >
              ★
            </span>
            <span style={{ fontSize: 'clamp(2.5rem, 10vw, 8rem)' }}>
              {titleTop}
            </span>
            <span 
              className="text-[#ffb400]"
              style={{ fontSize: 'clamp(2rem, 8vw, 8rem)' }}
            >
              ★
            </span>
          </div>
          {/* Subtitle - Responsive */}
          <div
            className="mt-1 font-extrabold text-slate-200 tracking-wider sm:tracking-[0.3em] md:tracking-[0.6em]"
            style={{ fontSize: 'clamp(1.2rem, 5vw, 3.5rem)' }}
          >
            {titleBottom}
          </div>
        </div>

        {/* 3D platform shelf - Responsive sizing */}
        <div className="mx-auto mt-4 sm:mt-6 w-full max-w-xs sm:max-w-2xl md:max-w-4xl lg:max-w-6xl origin-top [transform:perspective(800px)_rotateX(35deg)] sm:[transform:perspective(1000px)_rotateX(45deg)]">
          <div
            className="relative h-8 sm:h-12 md:h-16 lg:h-[62px] w-full rounded-[2px]"
            style={{
              border: '1px solid rgba(255,255,255,0.85)',
              borderWidth: 'clamp(1px, 0.2vw, 2px)',
              background:
                'repeating-linear-gradient(0deg, transparent, transparent 14px, rgba(255,255,255,0.85) 15px),' +
                'repeating-linear-gradient(90deg, transparent, transparent 14px, rgba(255,255,255,0.85) 15px)',
            }}
          />
        </div>

        {/* Info bar - Responsive */}
        <div className="mx-auto mt-2 sm:mt-3 w-full max-w-xs sm:max-w-2xl md:max-w-4xl lg:max-w-6xl px-2 sm:px-3">
          <div 
            className="rounded-[3px] border px-3 py-2 sm:px-4 sm:py-3 lg:px-5 lg:py-4 text-left"
            style={{ 
              backgroundColor: '#000', 
              borderColor: 'rgba(255,255,255,0.95)',
              fontSize: 'clamp(0.8rem, 2.5vw, 1.375rem)'
            }}
          >
            <span 
              className="mr-2 sm:mr-3 lg:mr-4 inline-flex items-center justify-center rounded-full bg-white text-black font-extrabold leading-none"
              style={{ 
                width: 'clamp(1.2rem, 3vw, 1.75rem)',
                height: 'clamp(1.2rem, 3vw, 1.75rem)',
                fontSize: 'clamp(0.6rem, 1.5vw, 1rem)'
              }}
            >
              !
            </span>
            Rate your <span className="font-extrabold">favorite players</span> and share your <em>honest opinions</em>. Help the community discover who's truly making an impact in sports!
          </div>
        </div>
      </header>

      {/* Cards grid - Fully responsive grid system */}
      <main className="mx-auto w-full max-w-none sm:max-w-2xl md:max-w-4xl lg:max-w-6xl xl:max-w-7xl px-3 sm:px-3 pb-8 sm:pb-12 md:pb-16 pt-4 sm:pt-6">
        {loadError ? (
          <div className="mx-auto max-w-md rounded-lg border border-red-400/40 bg-black/80 px-4 py-8 text-center">
            <p className="text-sm sm:text-base text-red-200">{loadError}</p>
            <button type="button" onClick={fetchPlayers} className="mt-4 rounded-md bg-white px-4 py-2 text-sm font-bold text-black hover:bg-slate-200">
              Try Again
            </button>
          </div>
        ) : players.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No players available yet.</p>
          </div>
        ) : (
          <div className="grid gap-2.5 sm:gap-3 md:gap-4 lg:gap-6
                          grid-cols-2
                          sm:grid-cols-2
                          md:grid-cols-3
                          lg:grid-cols-4
                          xl:grid-cols-4
                          2xl:grid-cols-5">
            {players.map((p, i) => (
              <Card
                key={p._id}
                player={p}
                idx={i}
                onClick={() => handleCardClick(p)}
              />
            ))}
          </div>
        )}
      </main>

    </div>
  );
};

export default PlayerReview;
